import React, {Fragment, useEffect} from 'react';
import {MARGIN_MD, SPAN_HALF, SPAN_ONE_THIRD, SPAN_TWO_THIRDS} from "../../styles/constants";
import {Card, Checkbox, Col, Divider, Form, Input, Row, Select} from "antd";
import {compose} from "redux";
import {connect} from "react-redux";
import {translate} from 'react-i18next';
import {getSdmxTypesTranslations} from "../../constants/getSdmxTypesTranslations";
import _ from "lodash";
import {DataLanguageConsumer} from "../../contexts/DataLanguage";
import {getLocalizedStr} from "../../middlewares/i18n/utils";
import {isIDValidWithHelp, isVersionValidWithHelp} from "../../utils/artefactValidators";
import PerfInput from "../perf-input";

export const LOGIN_OPTION_ACCOUNT = "account";

const mapStateToProps = state => ({
  endpoints: state.config.nodes,
  endpointId: state.app.endpointId,
  appLanguage: state.app.language,
  dataLanguages: state.config.dataManagement.dataLanguages,
  user: state.app.user
});

const mapPropsToFields = ({destination}) => ({
  endpoint: Form.createFormField({value: destination ? destination.endpoint : null}),
  username: Form.createFormField({value: destination ? destination.username : null}),
  password: Form.createFormField({value: destination ? destination.password : null}),
  id: Form.createFormField({value: destination ? destination.id : null}),
  agencyID: Form.createFormField({value: destination ? destination.agencyID : null}),
  version: Form.createFormField({value: destination ? destination.version : null}),
  withReferences: Form.createFormField({value: destination ? destination.withReferences : null}),
  loginOption: Form.createFormField({value: destination ? destination.loginOption : null}),
});

const formItemLayout = {
  labelCol: {span: SPAN_ONE_THIRD},
  wrapperCol: {span: SPAN_TWO_THIRDS}
};

const onFieldsChange = (props, fields) => props.onChange(_.mapValues(fields, ({value}) => value));
  
    
const ArtefactExportForm = ({
                              t,
                              form,
                              destination,
                              agencies,
                              appLanguage,
                              dataLanguages,
                              user,
                              endpoints,
                              endpointId,
                              type,
                              sourceTriplet,
                              isIdDisabled,
                              isVersionDisabled,
                              withReferences,
                              onChange
                            }) => {
                              
  const userHasProvider = user && user.providerId;
  const isReadOnlyNode = endpointId && !_.find(endpoints, node => node.general.id === endpointId).endpoint.haveDMWS;

  const node = (endpoints || []).find(node => node.general.id === form.getFieldValue('endpoint'));
  const supportedAuths = node ? node.supportedAuths || [] : [];

  const handleExternalLogin = (loginURL) => {
    localStorage.removeItem("loginCode");
    window.open(loginURL, "", "width=720,height=720");
  };

  useEffect(() => {
    function loginHandler(e) {
      const loginCode = localStorage.getItem("loginCode");
      if (e.storageArea === localStorage && loginCode) {
        onChange({providerLoginCode:loginCode})
        localStorage.removeItem("loginCode");
      }
    }

    window.addEventListener('storage', loginHandler);
    return () => {
      window.removeEventListener('storage', loginHandler);
    }
  }, [onChange]);

  return (
    <DataLanguageConsumer>
      {dataLanguage => {
        const lang = dataLanguage || appLanguage;
        
        return (
          <Fragment>
            <Card
              type="inner"
              title={t('components.exportArtefactForm.cards.source.title')}
              size="small"
              style={{marginBottom: MARGIN_MD}}
            >
              <Row style={{marginBottom: MARGIN_MD}}>
                <Col span={SPAN_HALF}>
                  <Form.Item
                    className="form-item-required"
                    label={t('components.exportArtefactForm.artefactType.label')}
                    {...formItemLayout}
                  >
                    <Input
                      disabled
                      value={
                        type
                          ? getSdmxTypesTranslations(t)[type]
                          : null
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={SPAN_HALF}>
                  <Form.Item
                    className="form-item-required"
                    label={t('data.artefact.id.label')}
                    {...formItemLayout}
                  >
                    <Input disabled value={sourceTriplet ? sourceTriplet.id : null}/>
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={SPAN_HALF}>
                  <Form.Item
                    className="form-item-required"
                    label={t('data.artefact.agencyID.label')}
                    {...formItemLayout}
                  >
                    <Select
                      showSearch
                      filterOption={(inputValue, {props}) =>
                        props.title.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      disabled
                      style={{width: "100%"}}
                      title={
                        agencies && sourceTriplet.agencyID &&
                        (getLocalizedStr((agencies[sourceTriplet.agencyID]), lang, dataLanguages) || sourceTriplet.agencyID)
                      }
                      value={sourceTriplet && sourceTriplet.agencyID}
                    >
                      {agencies
                        ? Object.keys(agencies).map((agencyID, index) => {

                          const agencyName = getLocalizedStr(agencies[agencyID], lang, dataLanguages);

                          return (
                            <Select.Option
                              key={index}
                              value={agencyID}
                              title={agencyID && agencyName ? `${agencyID} - ${agencyName}` : agencyID}
                            >
                              {agencyID && agencyName ? `${agencyID} - ${agencyName}` : agencyID}
                            </Select.Option>
                          );
                        })
                        : null
                      }
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={SPAN_HALF}>
                  <Form.Item
                    className="form-item-required"
                    label={t('data.artefact.version.label')}
                    {...formItemLayout}
                  >
                    <Input
                      disabled value={sourceTriplet ? sourceTriplet.version : null}
                      title={sourceTriplet ? sourceTriplet.version : null}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
            <Card
              type="inner"
              size="small"
              title={t('components.exportArtefactForm.cards.destination.title')}
            >
              <Row style={{marginBottom: MARGIN_MD}}>
                <Col span={SPAN_HALF}>
                  <Form.Item
                    {...formItemLayout}
                    className="form-item-required"
                    label={t('components.exportArtefactForm.destinationEndpoint.label')}
                  >
                    {form.getFieldDecorator('endpoint')(
                      <Select
                        showSearch
                        filterOption={(inputValue, {props}) =>
                          props.title.toLowerCase().includes(inputValue.toLowerCase())
                        }
                        style={{width: "100%"}}
                        onChange={value => {
                          onChange({loginOption:LOGIN_OPTION_ACCOUNT})
                        }}
                      >
                        {endpoints
                          .filter(endpoint => endpoint.general.id !== endpointId && endpoint.endpoint.haveDMWS)
                          .map(endpoint =>
                            <Select.Option
                              key={endpoint.general.id}
                              value={endpoint.general.id}
                              title={endpoint.general.name}
                            >
                              {endpoint.general.name}
                            </Select.Option>
                          )}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
                {isReadOnlyNode && supportedAuths.length > 0 && (
                  <Col span={SPAN_HALF}>
                    <Form.Item
                    {...formItemLayout}
                    className="form-item-required"
                    label={t('components.exportArtefactForm.loginOption.label')}
                    >
                      <Select 
                        style={{width: "100%"}}
                        value={form.getFieldValue('loginOption')}
                        onChange={value => {
                          onChange({loginOption:value})
                          if (value !== LOGIN_OPTION_ACCOUNT) {
                            const loginBaseURL = supportedAuths.find(supportedAuth => supportedAuth.providerName === value).loginBaseURL;
                            handleExternalLogin(loginBaseURL)
                          }
                        }}>
                        <Select.Option
                            key={LOGIN_OPTION_ACCOUNT}
                            value={LOGIN_OPTION_ACCOUNT}
                            title={t('components.exportArtefactForm.loginOption.values.account')}
                          >
                            {t('components.exportArtefactForm.loginOption.values.account')}
                          </Select.Option>
                        {supportedAuths
                          .map(({label, loginBaseURL, providerName}) =>
                            <Select.Option
                              key={providerName}
                              value={providerName}
                              title={label}
                            >
                              {label}
                            </Select.Option>
                        )}
                      </Select>
                      
                    </Form.Item>
                  </Col>
                )}
              </Row>
              {(!userHasProvider || isReadOnlyNode) && (form.getFieldValue('loginOption') === LOGIN_OPTION_ACCOUNT) && (
                <Fragment>
                  <Row>
                    <Col span={SPAN_HALF}>
                      <Form.Item
                        className="form-item-required"
                        label={t('components.exportArtefactForm.username.label')}
                        {...formItemLayout}
                      >
                        {form.getFieldDecorator('username')(
                          <PerfInput Component={Input}/>
                        )}
                      </Form.Item>
                    </Col>
                    <Col span={SPAN_HALF}>
                      <Form.Item
                        className="form-item-required"
                        label={t('components.exportArtefactForm.password.label')}
                        {...formItemLayout}
                      >
                        {form.getFieldDecorator('password')(
                          <PerfInput
                            Component={Input}
                            type={"password"}
                            autoComplete="new-password"/>
                        )}
                      </Form.Item>
                    </Col>
                  </Row>                  
                </Fragment>
              )}            
              <Divider/>
              <Row style={{marginBottom: MARGIN_MD}}>
                <Col span={SPAN_HALF}>
                  <Form.Item
                    className="form-item-required"
                    label={t('components.exportArtefactForm.artefactType.label')}
                    {...formItemLayout}
                  >
                    <Input
                      disabled
                      value={
                        type
                          ? getSdmxTypesTranslations(t)[type]
                          : null
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={SPAN_HALF}>
                  <Form.Item
                    className="form-item-required"
                    label={t('data.artefact.id.label')}
                    validateStatus={destination && isIDValidWithHelp(t, destination.id).help ? "warning" : null}
                    help={destination && isIDValidWithHelp(t, destination.id).help}
                    {...formItemLayout}
                  >
                    {form.getFieldDecorator('id', {
                      getValueFromEvent: e => (e || "").toUpperCase()
                    })(
                      <PerfInput
                        Component={Input}
                        disabled={isIdDisabled}
                        style={{textTransform: "uppercase"}}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={SPAN_HALF}>
                  <Form.Item
                    className="form-item-required"
                    label={t('data.artefact.agencyID.label')}
                    {...formItemLayout}
                  >
                    {form.getFieldDecorator('agencyID')(
                      <Select
                        showSearch
                        filterOption={(inputValue, {props}) =>
                          props.title.toLowerCase().includes(inputValue.toLowerCase())
                        }
                        style={{width: "100%"}}
                        title={
                          agencies && form.getFieldValue('agencyID') &&
                          (getLocalizedStr((agencies[form.getFieldValue('agencyID')]), lang, dataLanguages) || form.getFieldValue('agencyID'))
                        }
                      >
                        {agencies
                          ? Object.keys(agencies).map((agencyID, index) => {

                            const agencyName = getLocalizedStr(agencies[agencyID], lang, dataLanguages);

                            return (
                              <Select.Option
                                key={index}
                                value={agencyID}
                                title={agencyID && agencyName ? `${agencyID} - ${agencyName}` : agencyID}
                              >
                                {agencyID && agencyName ? `${agencyID} - ${agencyName}` : agencyID}
                              </Select.Option>
                            );
                          })
                          : null
                        }
                      </Select>
                    )}
                  </Form.Item>
                </Col>
                <Col span={SPAN_HALF}>
                  <Form.Item
                    className="form-item-required"
                    label={t('data.artefact.version.label')}
                    {...formItemLayout}
                    hasFeedback
                    validateStatus={destination && isVersionValidWithHelp(t, destination.version).help ? "warning" : null}
                    help={destination && isVersionValidWithHelp(t, destination.version).help}
                  >
                    {form.getFieldDecorator('version')(
                      <PerfInput
                        Component={Input}
                        title={form.getFieldValue('version')}
                        disabled={isVersionDisabled}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>
              {
                withReferences && (
                  <Row type="flex" justify="end" align="middle" style={{marginTop: MARGIN_MD}}>
                    <Col style={{marginRight: MARGIN_MD}}>
                      {t('components.exportArtefactForm.withReferences.label')}
                    </Col>
                    <Col>
                      <Form.Item>
                        {form.getFieldDecorator('withReferences', {valuePropName: 'checked'})(
                          <Checkbox/>
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                )
              }
            </Card>
          </Fragment>
        );
      }}
    </DataLanguageConsumer>
  )

}

export default compose(
  translate(),
  Form.create({mapPropsToFields, onFieldsChange}),
  connect(mapStateToProps)
)(ArtefactExportForm);



// WEBPACK FOOTER //
// ./src/components/artefact-export-form/index.jsx